import { useState } from "react";
import type { FormEvent } from "react";

import type { GlobalDataRecord, UpdateGlobalDriveFolderInput } from "../api";
import { ScopeFields } from "./GlobalDataTargetingFields";
import styles from "./GlobalDataTab.module.css";
import folderStyles from "./GlobalDriveFolder.module.css";

export type FolderSettings = Omit<
  UpdateGlobalDriveFolderInput,
  "global_data_id" | "excluded_subfolder_ids" | "exclude_file_ids" | "include_file_ids" | "names"
>;

const splitPatterns = (text: string): string[] =>
  text
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

interface Props {
  record: GlobalDataRecord;
  excludedCount: number;
  onSave: (settings: FolderSettings) => Promise<void>;
  onClose: () => void;
}

export const GlobalDriveFolderFormDialog = ({
  record,
  excludedCount,
  onSave,
  onClose,
}: Props) => {
  const saved = record.drive_sync;
  const [title, setTitle] = useState(record.title);
  const [tenantScope, setTenantScope] = useState(record.tenant_scope ?? "");
  const [landlordScope, setLandlordScope] = useState(record.landlord_scope ?? "");
  const [patternsText, setPatternsText] = useState(
    (saved?.exclude_name_patterns ?? []).join(", "),
  );
  const [enabled, setEnabled] = useState(saved?.enabled ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        tenant_scope: tenantScope.trim() || null,
        landlord_scope: landlordScope.trim() || null,
        exclude_name_patterns: splitPatterns(patternsText),
        enabled,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSaving(false);
      return;
    }
    onClose();
  };

  return (
    <div className={styles.dialogOverlay} onClick={onClose}>
      <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
        <div className={styles.dialogHead}>
          <div className={styles.dialogTitle}>Edit global folder</div>
          <button type="button" className={styles.linkBtn} onClick={onClose}>
            Close
          </button>
        </div>
        <form className={styles.dialogForm} onSubmit={onSubmit}>
          <label className={styles.field}>
            <span className={styles.label}>Title</span>
            <input
              className={styles.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Amazon design standards"
            />
          </label>

          <p className={folderStyles.helpText}>
            Drive folder &ldquo;{saved?.folder_name || record.original_name}&rdquo;, with{" "}
            {excludedCount} subfolder{excludedCount === 1 ? "" : "s"} excluded. Saving also
            saves the subfolders and files you ticked or unticked in the folder&apos;s tree.
          </p>

          <ScopeFields
            subject="folder"
            tenantScope={tenantScope}
            landlordScope={landlordScope}
            onTenantScopeChange={setTenantScope}
            onLandlordScopeChange={setLandlordScope}
          />

          <label className={styles.field}>
            <span className={styles.label}>Name exclusion patterns</span>
            <input
              className={styles.input}
              value={patternsText}
              onChange={(e) => setPatternsText(e.target.value)}
              placeholder="deprecated, archive"
            />
            <span className={folderStyles.helpText}>
              Comma-separated. Files (or folders on the path) whose name contains
              any of these are skipped. Case-insensitive substring match.
            </span>
          </label>

          <label className={folderStyles.checkboxLabel}>
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            Sync this folder every 15 minutes
          </label>

          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          <div className={styles.formActions}>
            <button
              type="submit"
              className={styles.submitBtn}
              disabled={title.trim() === "" || saving}
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button type="button" className={styles.cancelBtn} onClick={onClose}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
